import { useQuery } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_DASHBOARD, USE_MOCKS } from '@/mocks/fixtures'
import type { DashboardSummary } from '@/types/domain'

/* RE-EXPORTED, not declared. This module used to own a second range
 * vocabulary — `1W|1M|3M|1Y`, with hardcoded English labels and no `all` —
 * built for dashboard tiles that did not exist yet, while the finance page
 * filtered by `1M|3M|1Y|all`. Two vocabularies mean a founder can narrow the
 * dashboard to a week and the receivables behind it to a month and compare the
 * two without noticing (Story 6.3, plan step 7).
 *
 * The canonical set now lives with the shared control that renders it, and
 * `1W` is gone. The re-export stays because it is one name for one thing rather
 * than a second definition — anything that reaches for a range through the
 * dashboard's API module gets the same set the control renders.
 *
 * Its one consumer, KpiCard, was deleted on 2026-08-17: it wrapped a generic
 * tile shape the server does not send. The re-export outliving it is not an
 * oversight — the dashboard has no range control today (see useDashboard below),
 * and when it returns with the tiles it filters, this is the name it must use. */
export {
  DATE_RANGES,
  isDateRange,
  type DateRange,
} from '@/components/data/DateRangeControl'

/** The founder dashboard's figures.
 *
 *  Served by the read composer in cmd/asa/readmodel.go rather than by a module:
 *  the surface spans case movement and money, and neither module may hold both
 *  (AD-23). Restricted to Finance and Super-admin server-side — this hook does
 *  not decide that, and a role that reaches the endpoint anyway is refused
 *  rather than served a filtered response (FR-51, AD-5).
 *
 *  No `range` argument. It was passed when this surface was fixtures, and the
 *  fixture ignored it; the composer would ignore it too, because turnover — the
 *  one genuinely range-sensitive figure — is still pending (Story 5.5). A
 *  parameter nobody honours is a lie the next reader has to disprove, so it
 *  returns with the tile it filters. DateRange and DATE_RANGES stay exported
 *  above; see the note there. */
export function useDashboard() {
  return useQuery<DashboardSummary>({
    queryKey: queryKeys.dashboard.summary(),
    queryFn: () =>
      USE_MOCKS
        ? Promise.resolve(MOCK_DASHBOARD)
        : api.get<DashboardSummary>('/dashboard'),
  })
}
